import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Logo } from '../common';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';

const Sidebar = ({ isOpen, onClose, isCollapsed = false, onToggle, className = '' }) => {
  const location = useLocation();
  const { can } = usePermissions();
  const { user } = useAuth();
  const { companyData } = useCompany();
  const disabledItems = new Set([]);

  // Grouped navigation structure
  const navigationSections = [];

  // 1. SUPER ADMIN SECTION (Conditionally added)
  if (user?.role === 'SUPER_ADMIN') {
    navigationSections.push({
      section: 'Super Admin',
      items: [
        {
          name: 'Global Dashboard',
          href: '/superadmin/dashboard',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          ),
        },
        {
          name: 'All Companies',
          href: '/superadmin/companies',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          ),
        },
        {
          name: 'Manage All Users',
          href: '/superadmin/users',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ),
        },
        {
          name: 'Financials',
          href: '/superadmin/financials',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          ),
        },
      ],
    });
  }

  // 2. STANDARD SECTIONS
  navigationSections.push(
    {
      section: 'Overview',
      items: [
        {
          name: 'Dashboard',
          href: '/dashboard',
          permission: 'dashboard:view',
          icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z"
          />
        </svg>
          ),
        },
      ],
    },
    {
      section: 'Purchase & Vendor',
      items: [
        {
          name: 'Manage Purchases',
          href: '/purchases',
          permission: 'purchases:view',
          icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
          />
        </svg>
          ),
        },
        {
          name: 'Manage Vendors',
          href: '/vendors',
          permission: 'vendors:view',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          ),
        },
      ],
    },
    {
      section: 'Operations',
      items: [
        {
          name: 'Manage Expenses',
          href: '/expenses',
          permission: 'expenses:view',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          ),
        },
        {
          name: 'Manage Vehicles',
          href: '/vehicles',
          permission: 'dashboard:view', // Visible to all logged in users
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 13l2-5a2 2 0 011.873-1.265h8.254A2 2 0 0118 8l3 5v4a1 1 0 01-1 1h-1a2 2 0 11-4 0H9a2 2 0 11-4 0H4a1 1 0 01-1-1v-3z" />
            </svg>
          ),
        },
      ],
    },
    {
      section: 'Sales & Buyers',
      items: [
        {
          name: 'Manage Sales',
          href: '/sales',
          permission: 'sales:view',
          icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 3v18h18M7 13l4 4 6-8"
          />
        </svg>
          ),
        },
        {
          name: 'Manage Buyers',
          href: '/buyers',
          permission: 'customers:view',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          ),
        },
      ],
    },
    {
      section: 'Analytics',
      items: [
        {
          name: 'Reports',
          href: '/reports',
          permission: 'reports:view',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          ),
        },
      ],
    },
    {
      section: 'Administration',
      items: [
        {
          name: 'User Manager',
          href: '/users',
          permission: 'users:view',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A4 4 0 018 17h8a4 4 0 013 1.236M15 11a3 3 0 10-6 0 3 3 0 006 0z" />
            </svg>
          ),
        },
      ],
    },
  );

  const isActiveRoute = href => {
    return (
      location.pathname === href || location.pathname.startsWith(href + '/')
    );
  };

  // Persist open/closed state per section
  const storageKey = 'sidebar_open_sections_v2';
  const initialOpenState = useMemo(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    // default: all collapsed
    const map = {};
    navigationSections.forEach(g => { map[g.section] = false; });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [openSections, setOpenSections] = useState(initialOpenState);

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(openSections)); } catch {}
  }, [openSections]);

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-[#601e00] bg-opacity-50 transition-opacity lg:hidden z-20"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-30 bg-white shadow-lg transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isCollapsed ? 'w-16' : 'w-64'} ${className} relative`}
      >
        <div className="flex flex-col h-full">
          {/* Logo/Brand */}
          <div className="flex items-center h-16 px-4 border-b border-gray-200">
            {/* Logo - only show when not collapsed */}
            {!isCollapsed && (
              <div className="flex items-center flex-1">
                <Logo 
                  src={companyData.logo || "/images/logo-horizontal.png"}
                  size="xl" 
                  className="mr-2"
                  alt={`${companyData.name} Logo`}
                />
              </div>
            )}

            {/* Close button for mobile */}
            <button
              type="button"
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#fdbd0a]"
              onClick={onClose}
            >
              <span className="sr-only">Close sidebar</span>
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Collapse/Expand button - positioned vertically centered and half on sidebar, half on content */}
          <button
            type="button"
            className="hidden lg:flex absolute top-1/2 -translate-y-1/2 -right-4 w-8 h-8 rounded-full bg-[#601e00] text-white hover:bg-[#fdbd0a] hover:text-[#601e00] focus:outline-none focus:ring-2 focus:ring-[#fdbd0a] transition-all duration-200 shadow-lg items-center justify-center z-40 border-2 border-white"
            onClick={onToggle}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span className="sr-only">{isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}</span>
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={isCollapsed ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"}
              />
            </svg>
          </button>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-4 overflow-y-auto custom-scrollbar-thin">
            {navigationSections.map(group => (
              <div key={group.section}>
                {/* Section header with collapse/expand */}
                <details open={!!openSections[group.section]} className="group">
                  <summary
                    onClick={(e) => { e.preventDefault(); toggleSection(group.section); }}
                    className={`flex items-center justify-between px-3 ${isCollapsed ? 'hidden' : ''} cursor-pointer select-none outline-none focus:outline-none focus:ring-2 focus:ring-[#fdbd0a] rounded-sm`}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                      {group.section}
                    </span>
                    <svg className={`w-3 h-3 text-gray-600 transition-transform ${openSections[group.section] ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </summary>
                  <div className="mt-2 space-y-1">
                    {group.items
                      .filter(item => {
                        const hasPerm = !item.permission || can(item.permission);
                        return hasPerm;
                      })
                      .map(item => (
              <NavLink
                key={item.name}
                to={item.href}
            className={({ isActive }) => {
              const isDisabled = disabledItems.has(item.name);
              const base = `group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 focus:outline-none ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'focus:ring-2 focus:ring-[#fdbd0a]'} ${isCollapsed ? 'justify-center px-2' : ''}`;
              if (isActive || isActiveRoute(item.href)) {
                return `${base} ${isDisabled ? 'bg-gray-100 text-gray-400 border-r-2 border-gray-200' : 'bg-[#fff7e0] text-[#601e00] border-r-2 border-[#fdbd0a] hover:text-[#fdbd0a]'}`;
              }
              return `${base} ${isDisabled ? 'text-gray-400' : 'text-[#601e00] hover:bg-[#fff7e0] hover:text-[#fdbd0a] hover:border-r-2 hover:border-[#fdbd0a]'}`;
            }}
                onClick={(e) => {
                  const allowed = !item.permission || can(item.permission);
                  if (!allowed) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  // Close mobile menu when navigating
                  if (window.innerWidth < 1024) {
                    onClose();
                  }
                }}
                aria-disabled={disabledItems.has(item.name) ? true : undefined}
                title={isCollapsed ? item.name : ''}
              >
                <span
                  className={`${isCollapsed ? '' : 'mr-3'} flex-shrink-0 ${
                    disabledItems.has(item.name)
                      ? 'text-gray-300'
                      : isActiveRoute(item.href)
                        ? 'text-[#601e00]'
                        : 'text-[#601e00] group-hover:text-[#fdbd0a]'
                  }`}
                >
                  {item.icon}
                </span>
                {!isCollapsed && item.name}
              </NavLink>
                    ))}
                  </div>
                </details>
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-gray-200 p-4">
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              {!isCollapsed && (
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">User</p>
                  <p className="text-xs text-gray-500">View profile</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

Sidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isCollapsed: PropTypes.bool,
  onToggle: PropTypes.func,
  className: PropTypes.string,
};

export default Sidebar;
